import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import {
  DEFAULT_TEMPLATE_STATUSES,
  TEMPLATE_STATUSES,
  type TemplateStatus,
} from '@/types/saved-template';

// Deliberately narrower than `TemplateFilters.sortBy` in
// `saved-template.ts`, which also includes `'totalShifts'`. The old
// app's landing filter UI didn't expose total-shifts sorting; we keep
// parity here. If a future change reintroduces it, also add a branch
// to `applyFiltersAndSort` in `domain/templates/`.
export const SORT_OPTIONS = [
  'recentlyModified',
  'alphabetical',
  'recentlyCreated',
  'lastUsed',
] as const;
export type SortBy = (typeof SORT_OPTIONS)[number];

/** Page-size choices offered by the landing filter bar. */
export const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;

export interface LandingFilters {
  search: string;
  sites: string[];
  statuses: TemplateStatus[];
  sortBy: SortBy;
  pageSize: number;
}

const DEFAULT_SORT: SortBy = 'recentlyModified';
const DEFAULT_PAGE_SIZE = 10;

/**
 * Reads the templates-landing filter state from URL search params and
 * writes back via `setSearchParams`. Keeps filter state bookmarkable
 * and survives page refresh per A1 / D31.
 *
 * Schema:
 * - `?search=…`         — text search
 * - `?site=…` (repeated) — site filter (zero or more IDs)
 * - `?status=…` (repeated) — status filter; defaults to
 *   `['published', 'draft']` when absent, so deleted templates are excluded
 *   by default. Unknown values (e.g. a stale `archived`) are ignored
 * - `?sort=…`            — sort key (one of `SortBy`)
 *
 * URL updates use `replace: true` so typing in the search box doesn't
 * pollute browser history.
 */
export function useLandingFilters() {
  const [params, setParams] = useSearchParams();

  const filters: LandingFilters = useMemo(() => {
    const sites = params.getAll('site');
    const statusesRaw = params.getAll('status').filter(isStatus);
    const statuses = statusesRaw.length > 0 ? statusesRaw : [...DEFAULT_TEMPLATE_STATUSES];
    const sortRaw = params.get('sort');
    const sortBy = isSortBy(sortRaw) ? sortRaw : DEFAULT_SORT;
    const pageSizeRaw = Number(params.get('pageSize'));
    const pageSize = (PAGE_SIZE_OPTIONS as readonly number[]).includes(pageSizeRaw)
      ? pageSizeRaw
      : DEFAULT_PAGE_SIZE;
    return {
      search: params.get('search') ?? '',
      sites,
      statuses,
      sortBy,
      pageSize,
    };
  }, [params]);

  const setFilter = useCallback(
    <K extends keyof LandingFilters>(key: K, value: LandingFilters[K]) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (key === 'search') {
            const s = String(value ?? '');
            if (s) next.set('search', s);
            else next.delete('search');
          } else if (key === 'sites') {
            next.delete('site');
            (value as string[]).forEach((id) => next.append('site', id));
          } else if (key === 'statuses') {
            next.delete('status');
            (value as TemplateStatus[]).forEach((s) => next.append('status', s));
          } else if (key === 'sortBy') {
            next.set('sort', value as SortBy);
          } else if (key === 'pageSize') {
            next.set('pageSize', String(value));
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const clearAll = useCallback(() => {
    setParams(new URLSearchParams(), { replace: true });
  }, [setParams]);

  // True when any *filtering* is in effect — a search term, a site filter, or
  // a non-default status set. Sort order is deliberately excluded (it reorders
  // rather than filters). Drives the results-count + clear-all affordance.
  const hasActiveFilters = useMemo(
    () =>
      filters.search.length > 0 ||
      filters.sites.length > 0 ||
      filters.statuses.length !== DEFAULT_TEMPLATE_STATUSES.length ||
      !DEFAULT_TEMPLATE_STATUSES.every((status) => filters.statuses.includes(status)),
    [filters],
  );

  return { filters, setFilter, clearAll, hasActiveFilters };
}

function isStatus(s: string): s is TemplateStatus {
  return (TEMPLATE_STATUSES as readonly string[]).includes(s);
}

function isSortBy(s: string | null): s is SortBy {
  return s !== null && (SORT_OPTIONS as readonly string[]).includes(s);
}
